import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { API } from "../App";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Send, ShoppingBag, CheckCircle, MessageCircle, Loader2,
  Package, Check, CheckCheck, Clock
} from "lucide-react";
import { CustomerAuthContext } from "../components/CustomerLayout";
// [PROPOSITION-1] Import proposition card
import { CustomerPropositionCard } from "../components/chat/PropositionCard";

/* ─── Helpers ─── */
function dateSeparator(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (msgDay.getTime() === today.getTime()) return "Aujourd'hui";
  if (msgDay.getTime() === yesterday.getTime()) return "Hier";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long" });
}
function isSameDay(a, b) {
  if (!a || !b) return false;
  const da = new Date(a), db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
}
function timeStr(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export default function CustomerMessages() {
  const { customer, boutiqueName, devise, userId } = useContext(CustomerAuthContext);
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const token = localStorage.getItem(`customer_token_${userId}`);

  const boutiqueData = JSON.parse(localStorage.getItem("boutique") || "{}");
  const accent = boutiqueData.customColor || "#D4A853";

  const fetchConversation = useCallback(async () => {
    if (!token || !userId) return;
    try {
      const res = await axios.get(`${API}/conversations/${userId}/mine/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversation(res.data.conversation);
    } catch {
      setConversation(null);
    } finally {
      setLoading(false);
    }
  }, [token, userId]);

  useEffect(() => { fetchConversation(); }, [fetchConversation]);
  useEffect(() => {
    if (!token || !userId) return;
    const iv = setInterval(fetchConversation, 5000);
    return () => clearInterval(iv);
  }, [token, userId, fetchConversation]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation?.messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversation?.id) return;
    setSending(true);
    try {
      await axios.post(`${API}/conversations/${userId}/${conversation.id}/message`, {
        expediteur: "cliente", type: "texte", contenu: newMessage.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNewMessage("");
      fetchConversation();
      inputRef.current?.focus();
    } catch { /* silent */ }
    finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(e); }
  };

  // [PROPOSITION-1] + [PROPOSITION-2] Handle proposition response with optional message
  const handleRespondProposition = async (propId, reponse, messageCliente) => {
    try {
      await axios.put(`${API}/conversations/${userId}/${conversation.id}/proposition/${propId}/repondre`, {
        reponse,
        messageCliente: messageCliente || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchConversation();
    } catch { /* silent */ }
  };

  /* ─── Render messages ─── */
  const renderMessages = () => {
    const msgs = conversation?.messages || [];
    const els = [];
    let lastDate = null;

    msgs.forEach((msg, idx) => {
      if (!isSameDay(lastDate, msg.timestamp)) {
        els.push(
          <div key={`sep-${idx}`} style={{
            display: "flex", alignItems: "center", gap: 14, margin: "18px 0", padding: "0 4px",
          }}>
            <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
            <span style={{
              color: "#9CA3AF", fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
              textTransform: "uppercase", whiteSpace: "nowrap",
            }}>
              {dateSeparator(msg.timestamp)}
            </span>
            <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
          </div>
        );
        lastDate = msg.timestamp;
      }

      const isMe = msg.expediteur === "cliente";
      const time = timeStr(msg.timestamp);

      /* Confirmation */
      if (msg.type === "confirmation") {
        els.push(
          <motion.div key={msg.id || idx}
            initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="message-confirmation"
            style={{ display: "flex", justifyContent: "center", margin: "14px 0" }}>
            <div style={{
              background: "#F0FFF4", border: "1px solid #BBF7D0",
              borderRadius: 16, padding: "14px 20px", maxWidth: 280, textAlign: "center",
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: "50%", margin: "0 auto 8px",
                background: "#DCFCE7", display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <CheckCircle size={18} style={{ color: "#16A34A" }} />
              </div>
              <div style={{ color: "#16A34A", fontWeight: 700, fontSize: 13 }}>
                Commande confirmee !
              </div>
              <div style={{ color: "#374151", fontSize: 12, marginTop: 4 }}>{msg.contenu}</div>
              <div style={{ color: "#9CA3AF", fontSize: 10, marginTop: 8 }}>{time}</div>
            </div>
          </motion.div>
        );
        return;
      }

      // [PROPOSITION-1] Proposition — customer side
      if (msg.type === "proposition" && msg.proposition) {
        els.push(
          <CustomerPropositionCard
            key={msg.id || idx}
            msg={msg}
            accent={accent}
            userId={userId}
            onRespond={handleRespondProposition}
          />
        );
        return;
      }

      /* Product */
      if (msg.type === "produit" && msg.produit) {
        els.push(
          <motion.div key={msg.id || idx}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            data-testid="message-produit"
            style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start", margin: "8px 0" }}>
            <div style={{
              maxWidth: 280, borderRadius: 18, overflow: "hidden",
              background: "#fff", border: "1px solid #E5E7EB",
              boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
            }}>
              {msg.produit.image ? (
                <img src={msg.produit.image} alt={msg.produit.nom}
                  style={{ width: "100%", height: 150, objectFit: "cover" }} />
              ) : (
                <div style={{
                  width: "100%", height: 70,
                  background: `linear-gradient(135deg, ${accent}22, ${accent}11)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Package size={24} style={{ color: accent, opacity: 0.5 }} />
                </div>
              )}
              <div style={{ padding: "12px 16px" }}>
                <div style={{ color: "#1B1B1B", fontWeight: 700, fontSize: 14 }}>{msg.produit.nom}</div>
                {msg.produit.taille && (
                  <span style={{
                    display: "inline-block", background: "#F3F4F6",
                    borderRadius: 6, padding: "2px 8px", fontSize: 11, color: "#6B7280",
                    marginTop: 4,
                  }}>
                    Taille {msg.produit.taille}
                  </span>
                )}
                <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginTop: 6 }}>
                  <span style={{ color: "#F97316", fontWeight: 800, fontSize: 16 }}>
                    {msg.produit.prix?.toLocaleString("fr-FR")}
                  </span>
                  <span style={{ color: "#9CA3AF", fontSize: 12 }}>{msg.produit.devise || devise}</span>
                </div>
                <div style={{ color: "#9CA3AF", fontSize: 10, marginTop: 8, textAlign: "right" }}>{time}</div>
              </div>
            </div>
          </motion.div>
        );
        return;
      }

      /* Text */
      els.push(
        <motion.div key={msg.id || idx}
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          data-testid={`message-${msg.expediteur}`}
          style={{
            display: "flex", justifyContent: isMe ? "flex-end" : "flex-start",
            margin: "3px 0",
          }}>
          <div style={{
            maxWidth: "75%", padding: "10px 16px",
            background: isMe ? accent : "#fff",
            color: isMe ? "#fff" : "#1B1B1B",
            borderRadius: isMe ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
            boxShadow: isMe ? `0 2px 8px ${accent}33` : "0 1px 3px rgba(0,0,0,0.06)",
            border: isMe ? "none" : "1px solid #F3F4F6",
          }}>
            <div style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
              {msg.contenu}
            </div>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: isMe ? "flex-end" : "flex-start",
              gap: 4, marginTop: 4,
            }}>
              <span style={{
                color: isMe ? "rgba(255,255,255,0.5)" : "#9CA3AF",
                fontSize: 10,
              }}>{time}</span>
              {isMe && msg.lu && <CheckCheck size={12} style={{ color: "rgba(255,255,255,0.6)" }} />}
              {isMe && !msg.lu && <Check size={12} style={{ color: "rgba(255,255,255,0.35)" }} />}
            </div>
          </div>
        </motion.div>
      );
    });
    return els;
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <Loader2 size={24} className="animate-spin" style={{ color: accent }} />
      </div>
    );
  }

  if (!conversation) {
    return (
      <div data-testid="no-conversation" style={{ textAlign: "center", padding: "60px 24px" }}>
        <div style={{
          width: 64, height: 64, borderRadius: 20, margin: "0 auto 16px",
          background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <MessageCircle size={30} style={{ color: "#9CA3AF" }} />
        </div>
        <h3 style={{ color: "#1B1B1B", fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
          Pas encore de conversation
        </h3>
        <p style={{ color: "#9CA3AF", fontSize: 14, maxWidth: 280, margin: "0 auto", lineHeight: 1.5 }}>
          Parcourez la vitrine et cliquez sur &laquo; Je suis interessee &raquo; sur un produit pour demarrer.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="customer-messages-page" style={{
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 140px)",
      background: "#FAFAF7",
    }}>
      {/* Header */}
      <div style={{
        padding: "14px 20px", borderBottom: "1px solid #E5E7EB",
        background: "rgba(255,255,255,0.85)", backdropFilter: "blur(12px)",
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 2px 8px ${accent}33`,
        }}>
          <ShoppingBag size={18} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: "#1B1B1B", fontWeight: 700, fontSize: 15 }}>
            {boutiqueName || "La boutique"}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%", background: "#22C55E",
              display: "inline-block",
            }} />
            <span style={{ color: "#9CA3AF", fontSize: 12 }}>En ligne</span>
            {conversation.produitsDiscutes?.length > 0 && (
              <span style={{ color: "#D1D5DB", fontSize: 12 }}>
                &middot; {conversation.produitsDiscutes.length} produit(s)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflow: "auto", padding: "16px 16px" }}>
        {renderMessages()}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} style={{
        display: "flex", gap: 10, alignItems: "flex-end",
        padding: "12px 16px", borderTop: "1px solid #E5E7EB",
        background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)",
      }}>
        <div style={{
          flex: 1, display: "flex", alignItems: "flex-end",
          background: "#F3F4F6", borderRadius: 22,
          border: "1px solid #E5E7EB", padding: "4px 6px 4px 18px",
          transition: "border-color 200ms",
        }}>
          <textarea
            ref={inputRef}
            data-testid="message-input"
            value={newMessage}
            onChange={e => {
              setNewMessage(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px";
            }}
            onKeyDown={handleKeyDown}
            placeholder="Votre message..."
            rows={1}
            style={{
              flex: 1, background: "none", border: "none",
              padding: "10px 0", color: "#1B1B1B", fontSize: 14,
              outline: "none", resize: "none", maxHeight: 100, lineHeight: 1.45,
              fontFamily: "inherit",
            }}
          />
        </div>
        <button type="submit" disabled={!newMessage.trim() || sending}
          data-testid="send-message-btn"
          style={{
            width: 42, height: 42, borderRadius: 14, flexShrink: 0,
            background: newMessage.trim()
              ? `linear-gradient(135deg, ${accent}, ${accent}CC)`
              : "#E5E7EB",
            border: "none", cursor: newMessage.trim() ? "pointer" : "default",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 250ms",
            boxShadow: newMessage.trim() ? `0 4px 12px ${accent}33` : "none",
          }}>
          {sending
            ? <Loader2 size={16} className="animate-spin" color={newMessage.trim() ? "#fff" : "#9CA3AF"} />
            : <Send size={16} color={newMessage.trim() ? "#fff" : "#9CA3AF"} style={{ marginLeft: 1 }} />
          }
        </button>
      </form>
    </div>
  );
}
